import { request, container } from '@mtfe/sweet-utils';
import { addApiError, addCustomError, addApiXForbid, addLog } from '@mtfe/sweet-report';

import { setMTSIParams, useYodaInterceptor } from './oadYoda';
import { setH5GuardSign } from './oadH5Guard';
import { getFingerprint, getAppVersion } from './riskControl';

const { ajax } = request;

const { isInTuanApp, isInWaiMaiApp, isIos, isAndroid } = container;

function _handleError({ error, config, status, statusText, responseText, responseHeaders, requestHeaders }) {
    error.requestConfig = config;
    error.httpStatus = status;
    error.statusText = statusText;
    error.responseText = responseText;
    error.responseHeaders = responseHeaders;
    error.requestHeaders = requestHeaders;

    return error;
}

/**
 * 创建广告实例的工厂函数，支持滑块验证、H5Guard验签验证和风控参数的配置。
 *
 * @param {Object} options 配置选项对象。
 * @param {boolean} [options.useYoda=false] 是否使用滑块验证（mtsi校验）。默认不使用。
 * @param {boolean} [options.useH5Guard=false] 是否使用H5Guard验签验证。默认不使用。
 * @param {boolean} [options.useRisk=false] 是否开启风控。默认不使用。
 * @param {boolean} [options.needToLogan=false] 是否开启日志上报。默认不使用。
 *
 * @returns {Promise<Object>} 异步返回处理后的请求结果。
 */
async function adInstanceFactory(options) {
    const {
        useYoda = false,
        useH5Guard = false,
        useRisk = false, // 是否开启风控
    } = options || {};

    if (useYoda) {
        setMTSIParams(options.data);
    }

    if (useH5Guard) {
        options.headers = await setH5GuardSign(options);
    }

    if (useRisk) {
        options.data.riskParams = {};

        if (isIos) options.data.riskParams.fpPlatform = 5;
        if (isAndroid) options.data.riskParams.fpPlatform = 4;

        if (isInTuanApp || isInWaiMaiApp) {
            const [mt_fingerprint, app_version] = await Promise.all([getFingerprint(), getAppVersion()]);

            if (mt_fingerprint) options.data.riskParams.mtFingerprint = mt_fingerprint;

            if (app_version) options.data.appVersion = app_version;
        }
    }

    try {
        // 上报logan options （如果options存在：needToLogan）
        try {
            if (options && options.needToLogan) {
                addLog({
                    title: `${options.url} before request, options is : `,
                    tags: options,
                });
            }
        } catch (e) {
            console.error(e);
        }

        const requestSuccess = await ajax(options);

        // 上报logan requestSuccess （如果options存在：needToLogan）
        try {
            if (options && options.needToLogan) {
                addLog({
                    title: `${options.url} after request, response is : `,
                    tags: requestSuccess,
                });
            }
        } catch (e) {
            console.error(e);
        }

        return successInterceptor(requestSuccess);
    } catch (requestError) {
        throw errorInterceptor(requestError);
    }
}

/*
 * 响应拦截器：successInterceptor、errorInterceptor
 */
function successInterceptor(requestSuccess) {
    const { requestConfig, httpStatus, statusText, resData, responseText, responseHeaders, requestHeaders } = requestSuccess;
    const { checkRet = true, successRets = [0] } = requestConfig;

    if (resData && resData.code === 406) {
        useYodaInterceptor(resData.customData.requestCode);
        const error = _handleError({
            error: new Error('请先进行滑块验证'),
            config: requestConfig,
            status: httpStatus,
            statusText,
            responseText,
            responseHeaders,
            requestHeaders,
        });

        throw error;
    }

    if (resData && checkRet) {
        const { ret, msg: errorMsg, showMsg } = resData;

        if (!successRets.includes(ret)) {
            const error = _handleError({
                error: new Error(showMsg || errorMsg || '未知业务errorMsg'),
                config: requestConfig,
                status: httpStatus,
                statusText,
                responseText,
                responseHeaders,
                requestHeaders,
            });

            throw error;
        }
    }

    return Promise.resolve(resData);
}

function errorInterceptor(requestError) {
    const { requestConfig, httpStatus, statusText, message, responseText, requestHeaders, responseHeaders } = requestError;
    const { url, data: requestData } = requestConfig;
    const { rootPvId } = requestData || {};

    if (httpStatus === 403 && responseHeaders['Access-Control-Expose-Headers']?.toLowerCase() === 'x-forbid-reason') {
        // 上报风控拦截
        addApiXForbid(url, responseHeaders['X-Forbid-Reason'], {
            status: httpStatus,
            requestHeaders,
            responseHeaders,
            data: responseText,
            payload: requestConfig,
            rootPvId,
        });
    } else {
        if (httpStatus === 0) {
            addCustomError('请求中断', {
                status: httpStatus,
                requestHeaders,
                data: responseText,
                payload: requestConfig,
                rootPvId,
            });
        } else {
            const msg = message || `未知错误:${statusText}`;
            addApiError(url, msg, {
                status: httpStatus,
                requestHeaders,
                responseHeaders,
                data: responseText,
                payload: requestConfig,
                rootPvId,
            });
        }
    }

    throw requestError;
}

// post类型请求
function post(options) {
    return adInstanceFactory({
        ...options,
        method: 'POST',
    });
}

// get类型请求
function get(options) {
    return adInstanceFactory({
        ...options,
        method: 'GET',
    });
}

const oadInterceptor = {
    post,
    get,
};
export default oadInterceptor;
